import React from 'react';
import { useTranslation } from 'react-i18next';

function Pulse({ className = '' }) {
  return <div className={`bg-[#f0f2f4] animate-pulse rounded ${className}`} />;
}

function CardShell({ children, className = '' }) {
  return (
    <div className={`bg-white border border-[#e5e7eb] rounded-xl shadow-sm p-6 ${className}`}>
      {children}
    </div>
  );
}

export default function TeamsPlanningSkeleton() {
  const { t } = useTranslation('planning');
  return (
    <div className="space-y-4" aria-busy="true" aria-label={t('loadingTeamsPlanning')}>
      {/* Summary cards */}
      <div className="summary-cards tp-reveal tp-reveal-1">
        <div className="summary-cards-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white border border-[#e5e7eb] rounded-xl p-4 min-h-[8.25rem] flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <Pulse className="h-3 w-24" />
                <Pulse className="h-8 w-8 rounded-lg" />
              </div>
              <Pulse className="h-8 w-16" />
              <Pulse className="h-3 w-32" />
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#e5e7eb] rounded-xl p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Pulse className="h-9 w-9 rounded-lg shrink-0" />
              <div className="flex flex-col gap-2">
                <Pulse className="h-4 w-36" />
                <Pulse className="h-3 w-48" />
              </div>
            </div>
            <Pulse className="h-10 w-16 rounded-lg shrink-0" />
          </div>
          <Pulse className="h-2.5 w-full rounded-full" />
          <div className="flex justify-between">
            <Pulse className="h-3 w-20" />
            <Pulse className="h-3 w-16" />
          </div>
        </div>
      </div>

      {/* Workload Balance + Production Rates */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 tp-reveal tp-reveal-2">
        <CardShell>
          <div className="flex items-center justify-between mb-6">
            <Pulse className="h-5 w-48" />
            <div className="flex gap-2">
              <Pulse className="h-8 w-16 rounded-lg" />
              <Pulse className="h-8 w-16 rounded-lg" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Pulse key={i} className="h-6 w-20 rounded-full" />
            ))}
          </div>
          <Pulse className="h-[280px] w-full rounded-lg" />
        </CardShell>

        <CardShell>
          <Pulse className="h-5 w-36 mb-6" />
          <div className="flex flex-col items-center gap-6">
            <Pulse className="h-44 w-44 rounded-full" />
            <div className="w-full space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Pulse className="h-3 w-3 rounded-full shrink-0" />
                  <Pulse className="h-3 flex-1" />
                  <Pulse className="h-3 w-10 shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </CardShell>
      </div>

      {/* Resource Drift */}
      <div className="tp-reveal tp-reveal-3">
        <CardShell>
          <div className="flex items-center gap-3 mb-6">
            <Pulse className="h-10 w-10 rounded-xl shrink-0" />
            <div className="flex flex-col gap-2">
              <Pulse className="h-5 w-36" />
              <Pulse className="h-3 w-56" />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <Pulse className="h-44 w-44 rounded-full shrink-0" />
            <div className="flex-1 w-full space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-between">
                    <Pulse className="h-3 w-24" />
                    <Pulse className="h-3 w-12" />
                  </div>
                  <Pulse className="h-2.5 w-full rounded-full" />
                  <div className="flex justify-between">
                    <Pulse className="h-3 w-28" />
                    <Pulse className="h-3 w-8" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardShell>
      </div>

      {/* Pending declarations */}
      <div className="tp-reveal tp-reveal-4">
        <CardShell>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
            <div className="flex items-center gap-2">
              <Pulse className="h-8 w-8 rounded-md shrink-0" />
              <div className="flex flex-col gap-2">
                <Pulse className="h-5 w-52" />
                <Pulse className="h-3 w-64" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Pulse className="h-8 w-8 rounded-full" />
              <Pulse className="h-9 w-28 rounded-lg" />
            </div>
          </div>
          <Pulse className="h-10 w-full rounded-lg mb-5" />
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-1 sm:grid-cols-[1fr_1.1fr_0.7fr_0.7fr_auto] gap-3 sm:gap-4 items-center px-1 py-3"
              >
                <div className="flex items-center gap-3">
                  <Pulse className="h-9 w-9 rounded-full shrink-0" />
                  <div className="flex flex-col gap-2 flex-1 min-w-0">
                    <Pulse className="h-3 w-32" />
                    <Pulse className="h-2.5 w-24" />
                  </div>
                </div>
                <Pulse className="hidden sm:block h-3 w-36" />
                <Pulse className="hidden sm:block h-3 w-24" />
                <Pulse className="hidden sm:block h-3 w-20" />
                <Pulse className="h-9 w-32 rounded-lg justify-self-end" />
              </div>
            ))}
          </div>
        </CardShell>
      </div>
    </div>
  );
}
